/**
 * Admin Organizations Hook
 *
 * Direct Azure API queries for organization CRUD.
 * Only usable by super_admin role.
 *
 * AZURE MIGRATION: Now uses Azure API endpoints instead of Supabase
 */

import { useState, useEffect, useCallback } from 'react';
import { azureApiClient } from '../../lib/azureApiClient';
import { usePermissions } from '../usePermissions';

export const useAdminOrganizations = () => {
  const { isSuperAdmin } = usePermissions();
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all organizations with member and vessel counts
  const fetchOrganizations = useCallback(async () => {
    if (!isSuperAdmin) {
      setError('Access denied: Super Admin only');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call Azure API to get organizations with member and vessel counts
      const response = await azureApiClient.adminGetOrganizations(1, 100);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch organizations');
      }

      // Organizations come with member_count and vessel_count already calculated
      const orgsData = response.data || [];
      setOrganizations(orgsData);
    } catch (err) {
      console.error('[ADMIN] Error fetching organizations:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin]);

  // Initial fetch
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // Create organization
  const createOrganization = useCallback(async (orgData) => {
    if (!isSuperAdmin) {
      setError('Access denied: Super Admin only');
      return null;
    }

    try {
      // Call Azure API to create organization
      const response = await azureApiClient.adminCreateOrganization(orgData);

      if (!response.success) {
        throw new Error(response.error || 'Failed to create organization');
      }

      // Refresh the list
      await fetchOrganizations();
      return { organization: response.data, tempPassword: response.tempPassword, owner: response.owner };
    } catch (err) {
      console.error('[ADMIN] Error creating organization:', err);
      setError(err.message);
      return null;
    }
  }, [isSuperAdmin, fetchOrganizations]);

  // Update organization
  const updateOrganization = useCallback(async (orgId, updates) => {
    if (!isSuperAdmin) {
      setError('Access denied: Super Admin only');
      return null;
    }

    try {
      // FT-395 (Cluster C): use the ORG endpoint (PATCH /admin/organizations/:id).
      // Previously (hazard) called adminUpdateVessel(orgId, ...) — a VESSEL endpoint.
      const response = await azureApiClient.adminUpdateOrganization(orgId, updates);

      if (!response.success) {
        throw new Error(response.error || 'Failed to update organization');
      }

      // Refresh the list
      await fetchOrganizations();
      return response.data;
    } catch (err) {
      console.error('[ADMIN] Error updating organization:', err);
      setError(err.message);
      return null;
    }
  }, [isSuperAdmin, fetchOrganizations]);

  // Delete organization
  const deleteOrganization = useCallback(async (orgId) => {
    if (!isSuperAdmin) {
      setError('Access denied: Super Admin only');
      return false;
    }

    try {
      // FT-395 (Cluster C): use the ORG endpoint (DELETE /admin/organizations/:id).
      // Previously (hazard) called adminDeleteVessel(orgId) — a VESSEL endpoint that
      // could have deleted a real vessel. Deletion is super_admin-gated (guard above);
      // the caller (UI) must obtain an explicit confirm before invoking this.
      const response = await azureApiClient.adminDeleteOrganization(orgId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete organization');
      }

      // Refresh the list
      await fetchOrganizations();
      return true;
    } catch (err) {
      console.error('[ADMIN] Error deleting organization:', err);
      setError(err.message);
      return false;
    }
  }, [isSuperAdmin, fetchOrganizations]);

  return {
    organizations,
    isLoading,
    error,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    refresh: fetchOrganizations
  };
};

export default useAdminOrganizations;
